import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import { useEffect, useRef } from 'react';

/**
 * Six single-character inputs for OTP entry.
 *
 * Why native input elements: OTP entry needs per-digit focus, paste distribution
 * across boxes, and keyboard navigation (e.g. Backspace to previous box). Using
 * one character per native input (via MUI TextField) keeps that behavior simple
 * and predictable.
 *
 * Theme alignment: Native inputs don't have a Typography variant. Font for the
 * input is set from the theme via theme.typography.body1.fontSize and
 * theme.typography.body1.fontWeight in sx, so the OTP digits use the same body
 * font as the rest of the app and stay consistent with the design system.
 */
function OtpInput({ value, onChange, disabled }) {
  const { translateHubUi } = useHubTranslation();
  const inputsRef = useRef([]);

  useEffect(() => {
    if (typeof window !== 'undefined' && !disabled) {
      const firstInput = inputsRef.current[0];
      if (firstInput) {
        firstInput.focus();
      }
    }
  }, [disabled]);

  const handleChange = (event, index) => {
    const digit = event.target.value.replace(/[^0-9]/g, '').slice(0, 1);
    let otpArray = value.split('');
    otpArray[index] = digit;
    onChange(otpArray.join(''));
    if (digit && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();

    // Get clipboard data - supports both keyboard paste and context menu paste
    const pastedData = event.clipboardData?.getData('text') || '';

    // Extract only digits and remove whitespace/formatting
    const digits = pastedData.replace(/[^0-9]/g, '');

    // If no valid digits found, do nothing
    if (!digits) {
      return;
    }

    // Distribute digits into boxes in order (1-6), regardless of which box received the paste
    // Take only the first 6 digits if more are pasted
    const digitsToUse = digits.slice(0, 6);
    const otpDigits = digitsToUse.split('');

    // Pad to 6 characters if less than 6 digits were pasted
    while (otpDigits.length < 6) {
      otpDigits.push('');
    }

    // Update the value with all distributed digits
    const otpString = otpDigits.join('');
    onChange(otpString);

    // Focus the next empty box after paste, or the last box if all are filled
    const nextEmptyIndex = digitsToUse.length < 6 ? digitsToUse.length : 5;
    setTimeout(() => {
      inputsRef.current[nextEmptyIndex]?.focus();
    }, 0);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: 4,
        gap: { xs: 0.5, sm: 1.5, md: 2 },
      }}
    >
      {[...Array(6)].map((_, index) => (
        <TextField
          key={index}
          inputRef={(inputElement) => (inputsRef.current[index] = inputElement)}
          value={value[index] || ''}
          onChange={(event) => handleChange(event, index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            'aria-label': translateHubUi(
              'profile.otp.digitAriaLabel',
              'Digit {index} of 6',
              { index: index + 1 }
            ),
            style: {
              textAlign: 'center',
              padding: 0,
              width: '100%',
              height: '100%',
            },
          }}
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              background: theme.palette.background.paper,
              fontSize: theme.typography.body1.fontSize,
              fontWeight: theme.typography.body1.fontWeight,
              width: { xs: '100%', sm: 50, md: 58 },
              height: { xs: 45, sm: 50, md: 58 },
              p: 0,
              m: 0,
              textAlign: 'center',
              boxShadow: '0 2px 8px 0 rgba(120,130,140,0.06)', // subtle grey haze
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
              },
            },
            '& .MuiOutlinedInput-input': {
              textAlign: 'center',
              p: 0,
              m: 0,
              fontSize: theme.typography.body1.fontSize,
              fontWeight: theme.typography.body1.fontWeight,
              width: '100%',
              height: '100%',
              lineHeight: { xs: '45px', sm: '50px', md: '58px' },
            },
            '& .Mui-disabled': {
              background: '#F6F8FB',
              color: '#B0B8C4',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
          })}
          variant='outlined'
        />
      ))}
    </Box>
  );
}

export default OtpInput;
